import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Payment accounts, payments and transfers (A-01; docs/specs/payments.md). */
export const PAYMENT_ACCOUNT_TYPES = [
    "cash_drawer",
    "cash",
    "bank",
    "bkash",
    "nagad",
    "rocket",
    "other",
];
export const PAYMENT_METHODS = [
    "cash",
    "bkash",
    "nagad",
    "rocket",
    "bank_transfer",
    "card",
    "cheque",
    "other",
];
/** Mobile banking and cards are checked (by a gateway, or by hand against the statement); cash needs nothing. */
export const VERIFICATION_STATUSES = ["not_required", "pending", "verified", "failed"];
export const PAYMENT_DIRECTIONS = ["in", "out"];
export const paymentAccountTypeSchema = z.enum(PAYMENT_ACCOUNT_TYPES);
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const verificationStatusSchema = z.enum(VERIFICATION_STATUSES);
const cursorQuery = {
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
};
export const paymentAccountSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    type: paymentAccountTypeSchema,
    /** Null: tenant-wide (bank, wallets). Cash drawers belong to a register at a location. */
    locationId: z.uuid().nullable(),
    registerId: z.uuid().nullable(),
    /** Bank account or wallet number as typed (shown on screens only). */
    accountNumber: z.string(),
    openingBalancePaisa: paisa(),
    /** Opening balance plus money in, less money out (derived, never stored). */
    balancePaisa: paisa(),
    status: z.enum(["active", "archived"]),
    version: z.int(),
    createdAt: z.iso.datetime(),
});
export const paymentAccountListSchema = z.object({ data: z.array(paymentAccountSchema) });
export const createPaymentAccountRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    type: paymentAccountTypeSchema,
    locationId: z.uuid().nullable().default(null),
    registerId: z.uuid().nullable().default(null),
    accountNumber: z.string().trim().max(50).default(""),
    openingBalancePaisa: paisa().default(0),
})
    .strict()
    .refine((r) => r.type !== "cash_drawer" || (r.registerId !== null && r.locationId !== null), {
    message: "A cash drawer belongs to a register",
    path: ["registerId"],
});
export const updatePaymentAccountRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(100).optional(),
    accountNumber: z.string().trim().max(50).optional(),
    status: z.enum(["active", "archived"]).optional(),
    version: z.int().min(1),
})
    .strict();
export const paymentSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    accountId: z.uuid(),
    direction: z.enum(PAYMENT_DIRECTIONS),
    amountPaisa: paisa(),
    method: paymentMethodSchema,
    /** TrxID, cheque or transfer reference. */
    reference: z.string().nullable(),
    verificationStatus: verificationStatusSchema,
    orderId: z.uuid().nullable(),
    customerId: z.uuid().nullable(),
    /** `order`, `return`, `transfer`, `manual`, … — what the money is for. */
    sourceType: z.string(),
    sourceId: z.uuid().nullable(),
    reversalOfId: z.uuid().nullable(),
    reversed: z.boolean(),
    occurredAt: z.iso.datetime(),
    note: z.string(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
/** A recorded payment, plus earlier payments with the same method and reference (A-01: duplicate TrxID warns). */
export const recordedPaymentSchema = paymentSchema.extend({
    duplicates: z.array(z.object({ id: z.uuid(), number: z.string(), createdAt: z.iso.datetime() })),
});
export const paymentListQuerySchema = z.object({
    accountId: z.uuid().optional(),
    orderId: z.uuid().optional(),
    method: paymentMethodSchema.optional(),
    verificationStatus: verificationStatusSchema.optional(),
    reference: z.string().trim().min(1).max(60).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    ...cursorQuery,
});
export const paymentListSchema = z.object({ data: z.array(paymentSchema), nextCursor: z.uuid().nullable() });
/** `POST /v1/payments`: money in (from a customer, for an order) or out (a refund). */
export const recordPaymentRequestSchema = z
    .object({
    accountId: z.uuid(),
    direction: z.enum(PAYMENT_DIRECTIONS).default("in"),
    amountPaisa: paisa().min(1),
    method: paymentMethodSchema,
    reference: z.string().trim().min(1).max(60).nullable().default(null),
    orderId: z.uuid().nullable().default(null),
    customerId: z.uuid().nullable().default(null),
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    note: z.string().trim().max(500).default(""),
})
    .strict();
export const reversePaymentRequestSchema = z.object({ note: z.string().trim().min(1).max(500) }).strict();
export const verifyPaymentRequestSchema = z
    .object({ status: z.enum(["verified", "failed"]), note: z.string().trim().max(500).default("") })
    .strict();
export const accountTransferSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    fromAccountId: z.uuid(),
    toAccountId: z.uuid(),
    amountPaisa: paisa(),
    note: z.string(),
    occurredAt: z.iso.datetime(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const accountTransferListQuerySchema = z.object({ accountId: z.uuid().optional(), ...cursorQuery });
export const accountTransferListSchema = z.object({
    data: z.array(accountTransferSchema),
    nextCursor: z.uuid().nullable(),
});
export const createAccountTransferRequestSchema = z
    .object({
    fromAccountId: z.uuid(),
    toAccountId: z.uuid(),
    amountPaisa: paisa().min(1),
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    note: z.string().trim().max(500).default(""),
})
    .strict()
    .refine((r) => r.fromAccountId.toLowerCase() !== r.toAccountId.toLowerCase(), {
    message: "Pick two different accounts",
    path: ["toAccountId"],
});
/** Outbox data for `payments.payment.recorded` / `payments.payment.reversed`. */
export const paymentEventSchema = paymentSchema;
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "payments" };
const view = { permission: "payments.view" };
const record = { permission: "payments.record" };
const accounts = { permission: "payment_accounts.manage" };
export const listPaymentAccounts = defineEndpoint({
    ...tag,
    name: "listPaymentAccounts",
    method: "GET",
    path: "/v1/payment-accounts",
    access: view,
    summary: "Payment accounts with their balances (location accounts only at the caller's locations)",
    status: 200,
    response: paymentAccountListSchema,
});
export const getPaymentAccount = defineEndpoint({
    ...tag,
    name: "getPaymentAccount",
    method: "GET",
    path: "/v1/payment-accounts/{id}",
    access: view,
    summary: "One payment account with its balance",
    params: idParamsSchema,
    status: 200,
    response: paymentAccountSchema,
});
export const createPaymentAccount = defineEndpoint({
    ...tag,
    name: "createPaymentAccount",
    method: "POST",
    path: "/v1/payment-accounts",
    access: accounts,
    summary: "Add a cash drawer, bank account or mobile wallet",
    body: createPaymentAccountRequestSchema,
    status: 201,
    response: paymentAccountSchema,
    errors: ["LOCATION_INACTIVE"],
});
export const updatePaymentAccount = defineEndpoint({
    ...tag,
    name: "updatePaymentAccount",
    method: "PATCH",
    path: "/v1/payment-accounts/{id}",
    access: accounts,
    summary: "Rename or archive a payment account",
    params: idParamsSchema,
    body: updatePaymentAccountRequestSchema,
    status: 200,
    response: paymentAccountSchema,
    errors: ["CONFLICT"],
});
export const listPayments = defineEndpoint({
    ...tag,
    name: "listPayments",
    method: "GET",
    path: "/v1/payments",
    access: view,
    summary: "Payments, newest first (filter by account, order, method, TrxID, verification, dates)",
    query: paymentListQuerySchema,
    status: 200,
    response: paymentListSchema,
});
export const getPayment = defineEndpoint({
    ...tag,
    name: "getPayment",
    method: "GET",
    path: "/v1/payments/{id}",
    access: view,
    summary: "One payment",
    params: idParamsSchema,
    status: 200,
    response: paymentSchema,
});
export const recordPayment = defineEndpoint({
    ...tag,
    name: "recordPayment",
    method: "POST",
    path: "/v1/payments",
    access: record,
    summary: "Record money in or out of an account (an order's payment status follows)",
    body: recordPaymentRequestSchema,
    idempotent: true,
    status: 201,
    response: recordedPaymentSchema,
    errors: ["ACCOUNT_ARCHIVED"],
});
export const reversePayment = defineEndpoint({
    ...tag,
    name: "reversePayment",
    method: "POST",
    path: "/v1/payments/{id}/reverse",
    access: record,
    summary: "Cancel a payment with an opposite entry (payments are never edited)",
    params: idParamsSchema,
    body: reversePaymentRequestSchema,
    idempotent: true,
    status: 201,
    response: paymentSchema,
    errors: ["ALREADY_REVERSED"],
});
export const verifyPayment = defineEndpoint({
    ...tag,
    name: "verifyPayment",
    method: "POST",
    path: "/v1/payments/{id}/verification",
    access: { permission: "payments.verify" },
    summary: "Mark a mobile banking or card payment verified or failed (checked against the statement)",
    params: idParamsSchema,
    body: verifyPaymentRequestSchema,
    status: 200,
    response: paymentSchema,
    errors: ["INVALID_TRANSITION"],
});
export const listAccountTransfers = defineEndpoint({
    ...tag,
    name: "listAccountTransfers",
    method: "GET",
    path: "/v1/account-transfers",
    access: view,
    summary: "Money moved between accounts, newest first",
    query: accountTransferListQuerySchema,
    status: 200,
    response: accountTransferListSchema,
});
export const createAccountTransfer = defineEndpoint({
    ...tag,
    name: "createAccountTransfer",
    method: "POST",
    path: "/v1/account-transfers",
    access: { permission: "payment_accounts.transfer" },
    summary: "Move money between two accounts (e.g. drawer to bank deposit)",
    body: createAccountTransferRequestSchema,
    idempotent: true,
    status: 201,
    response: accountTransferSchema,
    errors: ["ACCOUNT_ARCHIVED"],
});
//# sourceMappingURL=payments.js.map